import { Routes } from '@angular/router';
import { ApplyComponent } from './components/views/apply/apply.component';
import { Error404Component } from './components/views/errors/error404/error404.component';
import { JobNoInterestComponent } from './components/views/job-no-interest/job-no-interest.component';
import { JobRecommendedComponent } from './components/views/job-recommended/job-recommended.component';
import { JobComponent } from './components/views/job/job.component';
import { JobboardComponent } from './components/views/jobboard/jobboard.component';
import { StandaloneIsaComponent } from './components/views/standalone-isa/standalone-isa.component';
import { StartPhoneCallComponent } from './components/views/start-phone-call/start-phone-call.component';
import { TalentpoolAcceptComponent } from './components/views/talentpool-accept/talentpool-accept.component';
import { UnsubscribeKiraMailChatComponent } from './components/views/unsubscribe-kira-mail-chat/unsubscribe-kira-mail-chat.component';
import { UnsubscribeMailComponent } from './components/views/unsubscribe-mail/unsubscribe-mail.component';
import { UnsubscribeTalentpoolComponent } from './components/views/unsubscribe-talentpool/unsubscribe-talentpool.component';
import { RenewTermsConsentComponent } from './components/views/renew-terms-consent/renew-terms-consent.component';
import { VerifyMailComponent } from './components/views/verify-mail/verify-mail.component';
import { MissingDataTrigger } from './enums/missing-data-trigger.enum';
import { ApplicationGuard } from './guards/application.guard';
import { hasMissingDataNonce } from './guards/missingData.guard';
import { CompanyInfoResolver } from './resolvers/company-info.resolver';
import { ISALinkResolver } from './resolvers/isa-link.resolver';
import { JobResolver } from './resolvers/job.resolver';
import { MissingDataResolver } from './resolvers/missing-data.resolver';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: JobboardComponent,
  },
  {
    path: 'companies/:companyId',
    component: JobboardComponent,
    resolve: { companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/apply',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    data: { type: 'initiative' },
    resolve: { companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/v2/apply',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    data: { type: 'initiative' },
    resolve: { companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/manual-application',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    data: { type: 'manual-initiative' },
    resolve: { companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/v2/manual-application',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    data: { type: 'manual-initiative' },
    resolve: { companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId',
    component: JobComponent,
    resolve: { job: JobResolver, companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/recommended',
    component: JobRecommendedComponent,
    resolve: { job: JobResolver, companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/no-interest',
    component: JobNoInterestComponent,
    resolve: { companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/unsubscribe-talentpool',
    redirectTo: 'unsubscribe-talentpool',
  },
  {
    path: 'companies/:companyId/jobs/:jobId/apply',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    canActivate: [hasMissingDataNonce],
    resolve: { job: JobResolver, companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/v2/apply',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    canActivate: [hasMissingDataNonce],
    resolve: { job: JobResolver, companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/verify',
    component: VerifyMailComponent,
    resolve: { companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/manual-application',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    data: { type: 'manual-application' },
    resolve: { job: JobResolver, companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/v2/manual-application',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    data: { type: 'manual-application' },
    resolve: { job: JobResolver, companyInfo: CompanyInfoResolver },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/applications/:applicationId/missing-data',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    canActivate: [hasMissingDataNonce],
    data: { type: 'missing-data' },
    resolve: {
      job: JobResolver,
      companyInfo: CompanyInfoResolver,
      missingData: MissingDataResolver,
    },
  },
  {
    path: `companies/:companyId/jobs/:jobId/applications/:applicationId/missing-data/${MissingDataTrigger.ACTIVE_SOURCING}`,
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    canActivate: [hasMissingDataNonce],
    data: { type: 'missing-data', trigger: MissingDataTrigger.ACTIVE_SOURCING },
    resolve: {
      job: JobResolver,
      companyInfo: CompanyInfoResolver,
      missingData: MissingDataResolver,
    },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/applications/:applicationId/missing-data/:trigger',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    canActivate: [hasMissingDataNonce],
    data: { type: 'missing-data' },
    resolve: {
      job: JobResolver,
      companyInfo: CompanyInfoResolver,
      missingData: MissingDataResolver,
    },
  },
  {
    path: 'companies/:companyId/jobs/:jobId/applications/:applicationId/v2/missing-data',
    component: ApplyComponent,
    canDeactivate: [ApplicationGuard],
    canActivate: [hasMissingDataNonce],
    data: { type: 'missing-data' },
    resolve: {
      job: JobResolver,
      companyInfo: CompanyInfoResolver,
      missingData: MissingDataResolver,
    },
  },
  {
    path: 'companies/:companyId/applications/:applicationId/talentpool-invitation',
    component: TalentpoolAcceptComponent,
  },
  {
    path: 'companies/:companyId/jobs/:jobId/applications/:applicationId/start-phone-call',
    component: StartPhoneCallComponent,
    resolve: { job: JobResolver, companyInfo: CompanyInfoResolver },
  },
  {
    path: 'unsubscribe/:applicationId',
    component: UnsubscribeMailComponent,
  },
  {
    path: 'unsubscribe-kira-mail-chat/:applicationId',
    component: UnsubscribeKiraMailChatComponent,
  },
  {
    path: 'unsubscribe-talentpool',
    component: UnsubscribeTalentpoolComponent,
  },
  {
    path: 'renew-terms-consent',
    component: RenewTermsConsentComponent,
  },
  {
    path: 'standalone-isa',
    component: StandaloneIsaComponent,
  },
  {
    path: 'standalone-isa/:linkId',
    component: StandaloneIsaComponent,
    resolve: { isaLink: ISALinkResolver },
  },
  {
    path: 'errors/404',
    component: Error404Component,
  },
  {
    path: '**',
    redirectTo: '/errors/404',
  },
];
